'use client';

import { cva } from 'class-variance-authority';
import {
  ReactNode,
  SelectHTMLAttributes,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from 'react';

import cn from '../lib/cn';

const hoverClassName = 'hover:border-1.5 hover:border-content-brand-secondary ';
const focusClassName =
  'focus-within:border-content-brand-secondary focus-within:border-1.5 ';
const filledClassName =
  'has-[input:not(:placeholder-shown)]:border-1.5 has-[input:not(:placeholder-shown)]:border-content-brand-secondary';

const wrapperVariants = cva(
  'relative flex justify-between w-full px-3 border border-dark-50 text-base font-normal gap-2.5 items-center bg-white group shadow-[1px_1px_2px_0_rgba(0,0,0,0.05)] transition-all rounded-lg',
  {
    variants: {
      size: {
        sm: 'h-8 rounded-lg',
        md: 'h-10 rounded-lg',
        lg: 'h-12 rounded-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

const selectVariants = cva(
  'absolute bg-white border rounded-lg z-10 left-0 w-full shadow-[0px_2px_10px_0px_rgba(0,0,0,0.1),0px_-1px_2px_0px_rgba(0,0,0,0.05)] p-1.5 text-base font-normal border border-dark-50 focus:outline-none focus:border-none focus:shadow-none focus:ring-0 placeholder:leading-normal placeholder:text-dark-200 disabled:pointer-events-none',
  {
    variants: {
      size: {
        sm: 'placeholder:text-sm text-sm',
        md: 'placeholder:text-base text-base',
        lg: 'placeholder:text-lg text-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

export type MultiSelectProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'size'
> & {
  options: any[];
  leftSection?: ReactNode;
  rightSection?: React.ReactNode;
  placeholder?: string;
  classNames?: {
    wrapper?: string;
    select?: string;
    error?: string;
    leftSection?: string;
    filterInput?: string;
  };
  error?: boolean;
  size?: 'sm' | 'md' | 'lg';
  value?: string[];
  onValueChange?: (value: string[]) => void;
  maxDisplay?: number;
  filterPlaceholder?: string;
  showFilter?: boolean;
};

const getClassName = ({
  error,
  disabled,
}: {
  error?: boolean;
  disabled?: boolean;
}) => {
  if (disabled) return 'cursor-not-allowed border-0.5 bg-dark-10 text-dark-200';
  if (error)
    return 'border-red-500 border-1.5 shadow-[0_0_4px_0_rgba(var(--color-red-500))]';
  return hoverClassName + ' ' + focusClassName + ' ' + filledClassName;
};

const MultiSelect = forwardRef<HTMLDivElement, MultiSelectProps>(
  (
    {
      options,
      leftSection,
      rightSection,
      classNames,
      className,
      error,
      placeholder = 'Select options',
      size = 'md',
      disabled,
      value,
      onValueChange,
      maxDisplay = 2,
      filterPlaceholder = 'Filter...',
      showFilter = true,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [internalSelected, setInternalSelected] = useState<string[]>([]);
    const [isDropUp, setIsDropUp] = useState(false);
    const [filterValue, setFilterValue] = useState('');
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    // Determine if component is controlled
    const isControlled = value !== undefined;

    // Use external value if controlled, internal state if uncontrolled
    const selected = isControlled ? value : internalSelected;

    // Initialize internal state only once for uncontrolled mode
    useEffect(() => {
      if (!isControlled && value !== undefined) {
        setInternalSelected(value);
      }
    }, []); // Empty dependency array - only run once on mount

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          setFilterValue(''); // Reset filter when closing
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    const toggleDropdown = (event?: React.MouseEvent) => {
      if (!disabled) {
        // Don't toggle if clicking on filter input or its container
        if (
          event &&
          (event.target as HTMLElement).closest('.filter-container')
        ) {
          return;
        }

        if (!isOpen && wrapperRef.current) {
          const rect = wrapperRef.current.getBoundingClientRect();
          const isAtBottom = rect.bottom + 200 > window.innerHeight;
          setIsDropUp(isAtBottom);
          // Don't reset filter when opening - let user continue typing
        }
        setIsOpen((prev) => !prev);
      }
    };

    const handleSelect = (option: any) => {
      const optionValue =
        typeof option === 'string' ? option : option.value || option;
      let newSelected: string[];

      if (selected.includes(optionValue)) {
        // Remove if already selected
        newSelected = selected.filter((item) => item !== optionValue);
      } else {
        // Add if not selected
        newSelected = [...selected, optionValue];
      }

      // Update internal state if uncontrolled
      if (!isControlled) {
        setInternalSelected(newSelected);
      }

      // Call the change handler if provided
      onValueChange?.(newSelected);
    };

    const handleOptionClick = (event: React.MouseEvent, option: any) => {
      event.preventDefault();
      event.stopPropagation();
      handleSelect(option);
    };

    const removeItem = (event: React.MouseEvent, itemToRemove: string) => {
      event.preventDefault();
      event.stopPropagation();
      const newSelected = selected.filter((item) => item !== itemToRemove);

      // Update internal state if uncontrolled
      if (!isControlled) {
        setInternalSelected(newSelected);
      }

      onValueChange?.(newSelected);
    };

    const getSelectedTags = () => {
      if (selected.length === 0) return null;

      // Show condensed view when we have more than maxDisplay selections to prevent overflow
      if (selected.length > maxDisplay) {
        return (
          <div className="flex items-center gap-1 overflow-hidden h-full">
            <span className="inline-flex items-center px-1.5 py-0.5 bg-gray-50 text-gray-800 text-xs rounded border border-gray-200 flex-shrink-0">
              {selected.length} items selected
            </span>
          </div>
        );
      }

      // Show individual tags when we have <= maxDisplay selections
      return (
        <div className="flex items-center gap-1 overflow-hidden h-full">
          {selected.map((item) => (
            <span
              key={item}
              className="inline-flex items-center gap-1 px-0.5 py-0.5 bg-gray-50 text-gray-800 text-xs rounded border border-gray-200 flex-shrink-0"
            >
              <span className="truncate max-w-12">{item}</span>
              <button
                type="button"
                onClick={(e) => removeItem(e, item)}
                className="text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-full w-3 h-3 flex items-center justify-center text-xs font-bold transition-colors flex-shrink-0"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      );
    };

    // Filter options based on the search input
    const filteredOptions = options.filter((option) => {
      const optionValue =
        typeof option === 'string' ? option : option.value || option;
      return optionValue.toLowerCase().includes(filterValue.toLowerCase());
    });

    return (
      <div
        className={cn(
          wrapperVariants({ size }),
          getClassName({ error, disabled }),
          classNames?.wrapper,
          className,
          'cursor-pointer',
        )}
        ref={(node) => {
          if (typeof ref === 'function') ref(node);
          wrapperRef.current = node;
        }}
        onClick={!disabled ? (e) => toggleDropdown(e) : undefined}
        onMouseDown={(e) => {
          // Prevent the dropdown from toggling when clicking on filter input
          if ((e.target as HTMLElement).closest('.filter-container')) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
      >
        <div className="flex gap-2 flex-1 min-w-0 items-center overflow-hidden">
          {leftSection}
          <div className="flex-1 min-w-0 overflow-hidden flex items-center">
            {selected.length > 0 ? (
              getSelectedTags()
            ) : (
              <div className="truncate">{placeholder}</div>
            )}
          </div>
        </div>
        {rightSection}

        {isOpen && (
          <div
            className={cn(
              selectVariants({ size }),
              isDropUp ? 'bottom-full mb-1' : 'top-full mt-1',
              'flex flex-col max-h-60',
            )}
          >
            {showFilter && (
              <div
                className="w-full border-b border-content-border flex-shrink-0"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  value={filterValue}
                  onChange={(e) => setFilterValue(e.target.value)}
                  placeholder={filterPlaceholder}
                  className={cn(
                    'w-full bg-transparent border-none outline-none focus:outline-none focus:ring-0 placeholder:text-content-tertiary',
                    size === 'sm' && 'placeholder:text-sm text-sm',
                    size === 'md' && 'placeholder:text-base text-base',
                    size === 'lg' && 'placeholder:text-lg text-lg',
                    classNames?.filterInput,
                  )}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <ul className="overflow-y-auto flex-1 min-h-0 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400">
              {filteredOptions.length === 0 && (
                <li className="p-2 text-center text-dark-300">
                  No options found
                </li>
              )}
              {filteredOptions.map((option: any) => {
                const optionValue =
                  typeof option === 'string' ? option : option.value || option;
                const isSelected = selected.includes(optionValue);

                return (
                  <li
                    key={optionValue}
                    onClick={(event) => handleOptionClick(event, option)}
                    className={cn(
                      'p-2 hover:bg-surface-hover cursor-pointer rounded-[4px] flex items-center gap-2 transition-colors',
                      isSelected && 'bg-surface-hover',
                    )}
                  >
                    <div
                      className={cn(
                        'flex items-center justify-center border border-gray-300 bg-white',
                        size === 'sm' && 'w-3.5 h-3.5',
                        size === 'md' && 'w-4 h-4',
                        size === 'lg' && 'w-5 h-5',
                      )}
                    >
                      {isSelected && (
                        <div className="w-full h-full bg-content-brand-primary flex items-center justify-center">
                          <svg
                            className={cn(
                              'text-content-brand-secondary',
                              size === 'sm' && 'w-2.5 h-2.5',
                              size === 'md' && 'w-3 h-3',
                              size === 'lg' && 'w-4 h-4',
                            )}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                    </div>
                    <span className="flex-1">{optionValue}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        <div className="flex w-fit max-w-fit flex-1 items-end cursor-pointer text-end">
          <svg
            className={cn(
              'flex w-fit max-w-fit flex-1 items-end cursor-pointer transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      </div>
    );
  },
);

MultiSelect.displayName = 'MultiSelect';

export default MultiSelect;
